import dayjs, { Dayjs } from 'dayjs'
import { Consult, ConsultSaveRequest, EmailTemplate, SalesUser, SchedulableProject, SchedulableProjects } from './customerApi'
import {
  buildConsultEmailBody,
  buildConsultEventDescription,
  buildConsultSummary,
  CONSULT_DATE_FORMAT,
  parseSalesLeadsToMap,
  selectedSalesLeadsToString,
} from './customersModel'

export const ConsultStatuses = {
  Scheduled: 0,
  Pending: 1,
  Sold: 2,
  Lost: 3,
  Canceled: 4,
} as const

const consultStatusLabels = ['Scheduled', 'Pending', 'Sold', 'Lost', 'Canceled']

export const consultStatusLabel = (status: number): string => consultStatusLabels[status] ?? ''

export type ConsultFormValues = {
  id: number
  customerId: number
  organizerEmail: string
  projectId: number | null
  projectName: string | null
  projectSold: boolean
  timeFrame: string
  bestTimeToCall: string
  consultationDate: Dayjs | null
  fee: string
  selectedSalesLeads: Record<string, boolean>
  cannedResponse: string
  summary: string
  status: number
  description: string
  notes: string
  emailBody: string
  createdBy: string | null
}

export const buildConsultFormValues = (
  consult: Consult | null,
  customerId: number,
  customerSalesLead?: string | null
): ConsultFormValues => {
  if (!consult) {
    return {
      id: 0,
      customerId,
      organizerEmail: '',
      projectId: null,
      projectName: null,
      projectSold: false,
      timeFrame: '',
      bestTimeToCall: '',
      consultationDate: null,
      fee: '',
      selectedSalesLeads: parseSalesLeadsToMap(customerSalesLead),
      cannedResponse: '',
      summary: '',
      status: ConsultStatuses.Scheduled,
      description: '',
      notes: '',
      emailBody: '',
      createdBy: null,
    }
  }

  return {
    id: consult.id,
    customerId: consult.customerId,
    organizerEmail: consult.organizerEmail ?? '',
    projectId: consult.projectId ?? null,
    projectName: consult.projectName ?? null,
    projectSold: consult.projectSold ?? false,
    timeFrame: consult.timeFrame ?? '',
    bestTimeToCall: consult.bestTimeToCall ?? '',
    consultationDate: consult.consultationDate ? dayjs(consult.consultationDate) : null,
    fee: consult.fee ?? '',
    selectedSalesLeads: parseSalesLeadsToMap(consult.salesLead),
    cannedResponse: consult.cannedResponse ?? '',
    summary: consult.summary ?? '',
    status: consult.status ?? ConsultStatuses.Scheduled,
    description: consult.description ?? '',
    notes: consult.notes ?? '',
    emailBody: consult.emailBody ?? '',
    createdBy: consult.createdBy ?? null,
  }
}

export const toConsultSaveRequest = (values: ConsultFormValues): ConsultSaveRequest => ({
  id: values.id,
  customerId: values.customerId,
  organizerEmail: values.organizerEmail,
  projectId: values.projectId,
  timeFrame: values.timeFrame,
  bestTimeToCall: values.bestTimeToCall,
  consultationDate: values.consultationDate ? values.consultationDate.format(CONSULT_DATE_FORMAT) : '',
  fee: values.fee,
  salesLead: selectedSalesLeadsToString(values.selectedSalesLeads) || null,
  cannedResponse: values.cannedResponse || null,
  summary: values.summary,
  status: values.status,
  description: values.description,
  notes: values.notes,
  emailBody: values.emailBody,
})

export const isAnySalesLeadSelected = (selectedSalesLeads: Record<string, boolean>): boolean =>
  Object.values(selectedSalesLeads).includes(true)

export const canSaveConsult = (values: ConsultFormValues): boolean =>
  !!values.organizerEmail && !!values.consultationDate && !!values.summary.trim() && isAnySalesLeadSelected(values.selectedSalesLeads)

export const allSchedulableProjects = (projects: SchedulableProjects | undefined): SchedulableProject[] => [
  ...(projects?.unscheduled ?? []),
  ...(projects?.scheduled ?? []),
]

export type ConsultAutoFillContext = {
  customer: {
    displayName: string
    address1?: string | null
    city?: string | null
    state?: string | null
    zip?: string | null
    phones: { number?: string | null }[]
    emails: { address?: string | null }[]
  }
  salesUsers: SalesUser[]
  projects: SchedulableProject[]
  cannedResponses?: EmailTemplate[] | null
}

export type ConsultAutoFillField = 'description' | 'emailBody' | 'summary'

export const regenerateConsultAutoFill = (
  values: ConsultFormValues,
  context: ConsultAutoFillContext,
  fields: ConsultAutoFillField[]
): ConsultFormValues => {
  const organizerName = context.salesUsers.find(u => u.email === values.organizerEmail)?.displayName ?? ''
  const projectName = context.projects.find(p => p.id === values.projectId)?.name ?? ''
  const consult = {
    consultationDate: values.consultationDate ? values.consultationDate.format(CONSULT_DATE_FORMAT) : '',
    cannedResponse: values.cannedResponse || null,
    projectId: values.projectId,
    selectedSalesLeads: values.selectedSalesLeads,
  }

  const next = { ...values }
  if (fields.includes('description')) {
    next.description = buildConsultEventDescription({
      customer: context.customer,
      consult,
      organizerName,
      projectName,
      cannedResponses: context.cannedResponses,
    })
  }
  if (fields.includes('emailBody')) {
    next.emailBody = buildConsultEmailBody({
      customerDisplayName: context.customer.displayName,
      consult,
      organizerName,
      cannedResponses: context.cannedResponses,
    })
  }
  if (fields.includes('summary')) {
    next.summary = buildConsultSummary({ consult, projectName, cannedResponses: context.cannedResponses })
  }
  return next
}

export const sortConsultsByDate = (consults: Consult[]): Consult[] =>
  [...consults].sort((a, b) => {
    const aDate = a.consultationDate ? dayjs(a.consultationDate).valueOf() : 0
    const bDate = b.consultationDate ? dayjs(b.consultationDate).valueOf() : 0
    return aDate - bDate
  })

export const upsertConsult = (consults: Consult[], saved: Consult): Consult[] =>
  consults.some(c => c.id === saved.id)
    ? consults.map(c => (c.id === saved.id ? saved : c))
    : [...consults, saved]
